import type { ReactNode } from "react";
import { IconChevronDown } from "@tabler/icons-react";
import { useText } from "../../clients/words";
import type { Razbor } from "../api";
import { ZnachokKanala } from "./ZnachokKanala";

/**
 * КОМУ, В КАКОЙ МЕССЕНДЖЕР И С КАКОЙ НАШЕЙ ЛИНИИ УЙДЁТ ОТВЕТ (`s720`, приёмка 06.10.2026).
 *
 * Слова задания: «перед отправкой явно показывать клиента, мессенджер и нашу линию/номер. Сейчас
 * в карточке СПб только „Ответ уйдёт: Лэш СПб“, нажатие ничего не делает».
 *
 * Было: строка называла ФИЛИАЛ КЛИЕНТКИ вместо линии (у общей линии сети своего названия на экране
 * не было) и выглядела кнопкой, а без второй беседы у клиентки не нажималась. Стало: три величины
 * стоят по отдельности — кому (имя и её аккаунт в мессенджере), мессенджер, наша линия (номер или
 * логин и чья она по настройкам). Линия — кнопка, только когда есть на что сменить; иначе это
 * просто слова, мёртвого нажатия нет.
 *
 * ⛔ ФИЛИАЛ ЛИНИИ — ИЗ НАСТРОЕК, НЕ ИЗ КЛИЕНТКИ. Сервер отдаёт их разными полями; общая линия сети
 * так и названа, клиентке СПб она «петербургской» не становится.
 */
export function PolosaKudaUydyot({
  dannye,
  mozhnoSmenit,
  onSmenit,
  zanyat,
  sprava,
}: {
  dannye: Razbor;
  /** Есть другая разрешённая линия для этой клиентки. */
  mozhnoSmenit: boolean;
  onSmenit: () => void;
  zanyat: boolean;
  /** Действие справа (в карточке лида — «Шаблоны»). */
  sprava?: ReactNode;
}) {
  const text = useText();
  const k = dannye.kuda_uydyot ?? null;
  const prezhde = dannye.otvet_uydyot_ot;

  const imya = k !== null ? k.komu.imya : dannye.shapka.customer_display;
  const akkaunt = k?.komu.akkaunt ?? null;
  const kind = k?.messendzher?.kind ?? prezhde.kanal_kind;
  const messendzher = k?.messendzher?.slovo ?? dannye.shapka.kanal_kind_display;
  const nomer = k?.liniya != null
    ? (k.liniya.adres !== "" ? k.liniya.adres : k.liniya.podpis)
    : (prezhde.adres !== "" ? prezhde.adres : (prezhde.kanal_podpis ?? prezhde.kanal_title));
  const chya = k?.liniya?.filial ?? null;

  const liniya = (
    <>
      <span>{text("obrashcheniya.kuda.s_linii")}:</span>{" "}
      <b className="font-semibold text-ops-text">{nomer}</b>
      {chya !== null && chya !== "" && <span> · {chya}</span>}
    </>
  );

  return (
    <div
      className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-ops-border px-3 pt-2 text-ops-caption text-ops-text-secondary"
      data-testid="kuda-uydyot"
    >
      <span className="min-w-0" data-testid="kuda-komu">
        <span>{text("obrashcheniya.kuda.komu")}:</span>{" "}
        <b className="font-semibold text-ops-text">{imya ?? text("obrashcheniya.kto_eto")}</b>
        {akkaunt !== null && akkaunt !== "" && akkaunt !== imya && <span> · {akkaunt}</span>}
      </span>

      <span className="inline-flex shrink-0 items-center gap-1.5 text-ops-text" data-testid="kuda-messendzher">
        <ZnachokKanala kind={kind} color={prezhde.color} razmer={18} />
        {messendzher}
      </span>

      {mozhnoSmenit ? (
        <button
          type="button"
          disabled={zanyat}
          onClick={onSmenit}
          aria-label={text("obrashcheniya.kuda.smenit")}
          title={text("obrashcheniya.kuda.smenit")}
          className="inline-flex min-w-0 items-center gap-1 rounded-ops-md border border-ops-border px-2 py-1 text-left hover:border-ops-primary disabled:opacity-50"
          data-testid="kuda-liniya"
          data-smena="da"
        >
          <span className="min-w-0">{liniya}</span>
          <IconChevronDown size={14} className="shrink-0" />
        </button>
      ) : (
        <span className="min-w-0" data-testid="kuda-liniya" data-smena="net">
          {liniya}
        </span>
      )}

      {sprava !== undefined && <span className="ml-auto shrink-0">{sprava}</span>}
    </div>
  );
}
